import React, { useCallback, useMemo } from 'react';
import timeAgo from '@/ctUtlis/timeAgo';
import { useStickyHeader } from '@/hooks/useStickyHeader';
import TooltipIcon from '@/components/Tooltip/TooltipIcon';

const isToday = (date) => {
  const today = new Date();
  return (
    date.day === today.getDate() &&
    parseInt(date.monthNumber) === today.getMonth() + 1 &&
    date.year === today.getFullYear()
  );
};

const isYesterday = (date) => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return (
    date.day === yesterday.getDate() &&
    parseInt(date.monthNumber) === yesterday.getMonth() + 1 &&
    date.year === yesterday.getFullYear()
  );
};

const List = ({
  DATATable,
  formattedDates,
  getTerminalIcon,
  getStatusBadge,
}) => {
  const { tableRef, theadRef } = useStickyHeader({
    appHeaderHeight: 64,
    enabled: true,
    transformProperty: 'translateY',
  });

  const terminalKeys = useMemo(() => Object.keys(DATATable), [DATATable]);

  const terminalCount = useMemo(() => terminalKeys.length, [terminalKeys]);

  const generateDateKey = useCallback((date) => {
    const day = date.day.toString().padStart(2, '0');
    return `${date.year}-${date.monthNumber}-${day}`;
  }, []);

  const getBorderColor = useCallback(
    (date, index) => {
      const isLastTerminal = index === terminalCount - 1;
      const isTodayDate = isToday(date);
      const isYesterdayDate = isYesterday(date);

      if ((isTodayDate || isYesterdayDate) && !isLastTerminal) {
        return '#99A0AE';
      }
      if (isTodayDate && isLastTerminal) {
        return '#99A0AE';
      }
      if (isYesterdayDate && isLastTerminal) {
        return '#99A0AE';
      }
      return '#E8E8E8';
    },
    [terminalCount]
  );

  const getBottomBorderColor = useCallback(
    (date, index) => {
      const isLastTerminal = index === terminalCount - 1;
      const isTodayDate = isToday(date);

      if (isTodayDate && isLastTerminal) {
        return '#99A0AE';
      }
      return '#E8E8E8';
    },
    [terminalCount]
  );

  const headerCells = useMemo(
    () =>
      formattedDates.map((date, index) => {
        const isTodayDate = isToday(date);
        const isYesterdayDate = isYesterday(date);

        return (
          <th
            key={index}
            style={{
              height: '31px',
              padding: '0',
              borderBottom: '1px solid #E8E8E8',
              borderRightColor:
                isTodayDate || isYesterdayDate ? '#99A0AE' : '#E8E8E8',
              borderTopColor: isTodayDate ? '#99A0AE' : '#E8E8E8',
            }}
            className={`${isTodayDate ? 'shadow-today' : ''} ${
              index === 6 && !isTodayDate ? 'border-r-0' : ''
            } border-solid border h-8 p-0 relative border-border-primary border-l-0 z-999 text-center   bg-white`}>
            <div className="flex flex-col items-center space-y-1">
              {index === 6 && isTodayDate && (
                <span
                  className="absolute top-0 right-0"
                  style={{
                    width: 1,
                    height: 1,
                    borderRight: '1px solid #99A0AE',
                    borderTop: '1px solid #99A0AE',
                  }}
                />
              )}
              <span
                className={`text-xs text-primary-text font-normal ${
                  date.dayName === 'Sun' || date.dayName === 'Sat'
                    ? 'text-gray-400'
                    : 'text-gray-700'
                } ${isTodayDate ? 'text-primary-blue font-bold' : ''}`}>
                {date.dayName} {parseInt(date.monthNumber)}/{date.day}
              </span>
            </div>
          </th>
        );
      }),
    [formattedDates]
  );

  const shiftHeaderCells = useMemo(
    () =>
      formattedDates.map((date, index) => {
        const isTodayDate = isToday(date);
        const isYesterdayDate = isYesterday(date);

        return (
          <th
            key={index}
            style={{
              borderTopColor: isTodayDate ? '#E8E8E8' : '',
              borderRightColor:
                isTodayDate || isYesterdayDate ? '#99A0AE' : '#E8E8E8',
            }}
            className={`${isTodayDate ? 'shadow-today' : ''} ${
              index === 6 && !isTodayDate ? 'border-r-0' : ''
            } border-solid border h-9 p-0 border-border-primary border-l-0 border-t-0 relative z-999 text-center text-xs font-normal text-primary-text  bg-white`}>
            <div className="flex justify-around">
              <span className="text-xs">1st</span>
              <span className="text-xs">2nd</span>
              <span className="text-xs">3rd</span>
            </div>
          </th>
        );
      }),
    [formattedDates]
  );

  const getStatusBgColor = useCallback((status) => {
    switch (status) {
      case 1:
        return 'bg-success-light text-success';
      case 0:
        return 'bg-error-light text-error-10';
      default:
        return '';
    }
  }, []);

  const TerminalRow = useCallback(
    ({ terminalKey, terminal, index }) => {
      const isBlurred = terminal.blur_status === 1;

      return (
        <tr key={terminalKey} className={isBlurred ? '' : 'hover:'}>
          <td
            style={{
              height: '50px',
              width: 132,
              borderRightColor: isToday(formattedDates[0])
                ? '#99A0AE'
                : '#E8E8E8',
            }}
            className="p-0 px-2 py-1 border-solid border-t-0 whitespace-nowrap border-l-0 bg-white border border-border-primary">
            <a
              className="py-1 rounded hover:bg-border-light-secondary no-underline flex flex-col items-center "
              href={terminal.links?.gate_schedule}
              target="_blank"
              rel="noreferrer">
              <img
                src={getTerminalIcon(terminalKey)}
                alt={terminalKey}
                className="max-h-20 object-contain"
                style={{
                  maxWidth: '86px',
                }}
              />
              <span className="text-10 text-gray-600 mt-0">
                {isBlurred
                  ? 'No Data'
                  : terminal.gate_schedule_last_update?.updated_at
                  ? timeAgo(terminal.gate_schedule_last_update.updated_at)
                  : 'No Data'}
              </span>
            </a>
          </td>

          {formattedDates.map((date, dateIndex) => {
            const dateKey = generateDateKey(date);
            const scheduleData = terminal.gate_schedules?.[dateKey];
            const isTodayDate = isToday(date);

            return (
              <td
                key={dateIndex}
                className={`px-2  whitespace-nowrap border-t-0 border border-solid border-l-0 ${
                  isTodayDate ? 'shadow-today' : ''
                } ${
                  isBlurred
                    ? 'bg-empty-table-cell bg-border-light-secondary'
                    : ''
                }`}
                style={{
                  borderRightColor: getBorderColor(date, index),
                  padding: isBlurred ? '0' : '',
                  borderBottomColor: getBottomBorderColor(date, index),
                  borderRightWidth: dateIndex === 6 && !isTodayDate ? '0' : '',
                  backgroundSize: '12px 12px',
                }}>
                {!isBlurred && (
                  <div className="flex justify-around space-x-1">
                    <div className="flex items-end justify-end flex-col">
                      {scheduleData?.info?.shift1_message &&
                        scheduleData?.shift1 !== null && (
                          <span
                            className={`flex items-center justify-center w-4 h-4 rounded-full  -mb-2 -mr-1`}>
                            <TooltipIcon
                              width={12}
                              height={12}
                              iconId={`shift1-${dateKey}-${terminalKey}`}
                              content={scheduleData?.info?.shift1_message}
                              arrow
                              tooltipId={`shift1-${dateKey}-${terminalKey}`}
                            />
                          </span>
                        )}
                      {getStatusBadge(scheduleData?.shift1)}
                    </div>

                    <div className="flex items-end justify-end flex-col">
                      {scheduleData?.info?.shift2_message &&
                        scheduleData?.shift2 !== null && (
                          <span
                            className={`flex items-center justify-center w-4 h-4 rounded-full  -mb-2 -mr-1`}>
                            <TooltipIcon
                              width={12}
                              height={12}
                              iconId={`shift2-${dateKey}-${terminalKey}`}
                              content={scheduleData?.info?.shift2_message}
                              arrow
                              tooltipId={`shift2-${dateKey}-${terminalKey}`}
                            />
                          </span>
                        )}
                      {getStatusBadge(scheduleData?.shift2)}
                    </div>

                    <div className="flex items-end justify-end flex-col">
                      {scheduleData?.info?.shift3_message &&
                        scheduleData?.shift3 !== null && (
                          <span
                            className={`flex items-center justify-center w-4 h-4 rounded-full  -mb-2 -mr-1`}>
                            <TooltipIcon
                              width={12}
                              height={12}
                              iconId={`shift3-${dateKey}-${terminalKey}`}
                              content={scheduleData?.info?.shift3_message}
                              arrow
                              tooltipId={`shift3-${dateKey}-${terminalKey}`}
                            />
                          </span>
                        )}
                      {getStatusBadge(scheduleData?.shift3)}
                    </div>
                  </div>
                )}
              </td>
            );
          })}
        </tr>
      );
    },
    [
      formattedDates,
      getTerminalIcon,
      generateDateKey,
      getBorderColor,
      getBottomBorderColor,
      getStatusBadge,
    ]
  );

  const terminalRows = useMemo(
    () =>
      terminalKeys.map((terminalKey, index) => (
        <TerminalRow
          key={terminalKey}
          terminalKey={terminalKey}
          terminal={DATATable[terminalKey]}
          index={index}
        />
      )),
    [terminalKeys, DATATable, TerminalRow]
  );

  return (
    <div
      ref={tableRef}
      className="relative bg-white rounded-md shadow-sm border border-b-0 border-t-0 border-solid border-border-primary overflow-hidden">
      <div className="w-full overflow-x-auto">
        <table
          className="border-separate  table-fixed lg:min-w-full min-w-5xl"
          style={{
            borderSpacing: 0,
          }}>
          <thead ref={theadRef} className="bg-white">
            <tr>
              <th
                key="terminal-header"
                style={{
                  height: '68px',
                  borderRightColor: isToday(formattedDates[0])
                    ? '#99A0AE'
                    : '#E8E8E8',
                }}
                rowSpan={2}
                className="border-solid border border-l-0 h-12 p-0 border-border-primary text-center text-xs text-primary-text ">
                Terminal
              </th>
              {headerCells}
            </tr>
            <tr>{shiftHeaderCells}</tr>
          </thead>
          <tbody className="bg-white">{terminalRows}</tbody>
        </table>
      </div>
    </div>
  );
};

export default React.memo(List);
